import { useState, useCallback } from 'react';
import { useListFetch, useModal, useRequest } from '../../../../../hooks';
import { notify } from '../../../../../app';
import {
  getKnowledgeSourceChunks,
  searchKnowledge,
  deleteChunks,
} from '../../../../../api';
import type { SearchKnowledgeResponse } from '../../../../../api';
import { useRouteMatch } from 'react-router-dom';

export type FormProps = {
  search: string | null;
  limit?: number | string;
  threshold?: number | string;
};

const DEFAULT_NUM_RESULTS = 3;
const DEFAULT_THRESHOLD = 0;

const defaultFormValues: FormProps = {
  search: '',
  limit: DEFAULT_NUM_RESULTS,
  threshold: DEFAULT_THRESHOLD,
};

export const useKnowledgeSourceDetails = (dataSourceId: string = '') => {
  const [form, setForm] = useState<FormProps>(defaultFormValues);
  const [pageDetails, setPageDetails] = useState(1);
  const [searchResult, setSearchResult] =
    useState<SearchKnowledgeResponse | null>(null);
  const [selectedChunkIds, setSelectedChunkIds] = useState<Set<string>>(
    new Set()
  );
  const {
    isOpened: isDeleteConfirmOpened,
    onOpen: onOpenDeleteConfirm,
    onClose: onCloseDeleteConfirm,
  } = useModal();
  const { params } = useRouteMatch<{ id: string }>();

  const knowledgeBaseId = params?.id || '';

  const {
    nodes: dataChunks,
    filters,
    setFilters,
    setSort,
    intializing: loadingChunks,
    refresh,
    ...rest
  } = useListFetch(
    async queryParams => {
      if (!dataSourceId) return;

      return await getKnowledgeSourceChunks({
        knowledgeBaseId,
        dataSourceId,
        page: pageDetails,
        limit: queryParams?.pagination?.limit || 20,
      });
    },
    {
      deps: [params?.id, dataSourceId, pageDetails],
      resultKey: 'chunks',
      withQueryParams: true,
    }
  );

  const [handleSearchKnowledgeRequest, searchingKnowledge] = useRequest(
    _signal => {
      const parsedLimit =
        form.limit === undefined || form.limit === null
          ? DEFAULT_NUM_RESULTS
          : Number(form.limit);
      const num_results: number = Number.isFinite(parsedLimit)
        ? parsedLimit
        : DEFAULT_NUM_RESULTS;

      const parsedThreshold =
        form.threshold === undefined || form.threshold === null
          ? DEFAULT_THRESHOLD
          : Number(form.threshold);
      const threshold: number = Number.isFinite(parsedThreshold)
        ? parsedThreshold
        : DEFAULT_THRESHOLD;

      return searchKnowledge({
        query: form.search ?? '',
        knowledge_base_id: knowledgeBaseId,
        num_results,
        threshold,
      });
    },
    {
      onSuccess: data => {
        setSearchResult(data);
      },
      onError: () => {
        notify.error('Failed to search the knowledge');
      },
      deps: [form, knowledgeBaseId],
    }
  );

  const [handleDeleteChunksRequest, deletingChunks] = useRequest(
    _signal => {
      return deleteChunks({
        knowledgeBaseId,
        ids: Array.from(selectedChunkIds),
      });
    },
    {
      onSuccess: () => {
        notify.success('Selected chunks deleted successfully');
        setSelectedChunkIds(new Set());
        onCloseDeleteConfirm();
        refresh();
      },
      onError: () => {
        notify.error('Failed to delete selected chunks');
      },
      deps: [selectedChunkIds],
    }
  );

  const handleSelectChunk = useCallback((chunkId: string, checked: boolean) => {
    setSelectedChunkIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(chunkId);
      } else {
        next.delete(chunkId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((allIds: string[], checked: boolean) => {
    if (checked) {
      setSelectedChunkIds(new Set(allIds));
    } else {
      setSelectedChunkIds(new Set());
    }
  }, []);

  const resetSearch = () => {
    setForm(defaultFormValues);
    setSearchResult(null);
  };

  const resetSelection = useCallback(() => {
    setSelectedChunkIds(new Set());
  }, []);

  return {
    handleSearchKnowledge: handleSearchKnowledgeRequest,
    searchingKnowledge,
    searchResult,
    setForm,
    form,
    dataChunks,
    filters,
    setFilters,
    setSort,
    pageDetails,
    setPageDetails,
    loadingChunks,
    refresh,
    resetSearch,
    selectedChunkIds,
    handleSelectChunk,
    handleSelectAll,
    isDeleteConfirmOpened,
    onOpenDeleteConfirm,
    onCloseDeleteConfirm,
    handleDeleteChunks: handleDeleteChunksRequest,
    deletingChunks,
    resetSelection,
    ...rest,
  };
};
